import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  getDocs
} from 'firebase/firestore';
import { db } from './firebase';
import { UserProfile, PackageItem, ShipmentOrder } from '../types';

export interface AffiliateProgramConfig {
  programActive: boolean;
  referrerRewardJpy: number; // e.g., 1000 JPY credit when referred friend completes 1st shipment or VIP plan
  referredWelcomeBonusJpy: number; // e.g., 500 JPY welcome freight discount for referred friend
  proServiceFeeCommissionPercent: number; // e.g., 20% on Personal Shopper / Redirection fee for PRO influencers
  proSubscriptionCommissionPercent: number; // e.g., 25% on PRO/Business subscription plans
  minPayoutJpy: number; // e.g., 5000 JPY for PIX payout (or 500 JPY for freight credit)
  updatedAt: string;
}

export type ReferralStatus = 'pendente' | 'qualificado' | 'pago';
export type AffiliateTier = 'client' | 'influencer';

export interface AffiliateReferral {
  id: string;
  referrerSuite: string;
  referrerUid: string;
  referrerName: string;
  referredSuite: string;
  referredUid: string;
  referredName: string;
  referredEmail: string;
  status: ReferralStatus;
  rewardJpy: number;
  welcomeBonusJpy: number;
  tier: AffiliateTier;
  notes?: string;
  createdAt: string;
  qualifiedAt?: string;
}

export interface AffiliatePayoutRequest {
  id: string;
  suiteNumber: string;
  userId: string;
  userName: string;
  amountJpy: number;
  redemptionType: 'freight_credit' | 'pix';
  pixKey?: string;
  couponCode?: string;
  status: 'pendente' | 'aprovado' | 'recusado';
  createdAt: string;
  processedAt?: string;
}

export type GiveawayEligibilityRule = 'all_suites' | 'active_packages' | 'vip_only';
export type GiveawayStatus = 'Ativo' | 'Sorteado' | 'Entregue na Suíte';

export interface SuiteGiveaway {
  id: string;
  title: string;
  description: string;
  prizeTitle: string;
  prizeValueJpy: number;
  prizeWeightKg: number;
  prizeImageUrl?: string;
  drawDate: string;
  eligibilityRule: GiveawayEligibilityRule;
  vipMultiplierEnabled: boolean;
  status: GiveawayStatus;
  winnerSuiteNumber?: string;
  winnerUserId?: string;
  winnerUserName?: string;
  drawnAt?: string;
  packageDeposited?: boolean;
  createdPackageId?: string;
  manualParticipantSuites?: string[]; // Optional extra suites added manually
  createdAt: string;
}

export interface EligibleSuiteEntry {
  uid: string;
  suiteNumber: string;
  name: string;
  email: string;
  plan: 'free' | 'pro' | 'business';
  packagesCount: number;
  shipmentsCount: number;
  ticketsCount: number;
  eligibilityReason: string;
}

const STORAGE_KEYS = {
  CONFIG: 'dojapao_affiliate_config_v1',
  REFERRALS: 'dojapao_affiliate_referrals_v1',
  PAYOUTS: 'dojapao_affiliate_payouts_v1',
  GIVEAWAYS: 'dojapao_suite_giveaways_v1',
  CAPTURED_REF: 'dojapao_captured_ref_suite_v1'
};

export const DEFAULT_AFFILIATE_CONFIG: AffiliateProgramConfig = {
  programActive: true,
  referrerRewardJpy: 1000,
  referredWelcomeBonusJpy: 500,
  proServiceFeeCommissionPercent: 20,
  proSubscriptionCommissionPercent: 25,
  minPayoutJpy: 3000,
  updatedAt: '2026-09-28T00:00:00.000Z'
};

export const DEFAULT_SUITE_GIVEAWAYS: SuiteGiveaway[] = [
  {
    id: 'giveaway-current-month',
    title: 'Sorteio Oficial das Suítes: Box Especial Akihabara + Booster Pokémon TCG Japonês',
    description:
      'Todos os clientes com Suíte ativa concorrem automaticamente! O prêmio é depositado direto na sua Suíte em Saitama com Taxa de Serviço ¥0 para ser enviado junto com sua próxima consolidação.',
    prizeTitle: 'Box 15 Snacks Exclusivos do Japão + 2 Boosters Pokémon Terastal Festival ex (JP)',
    prizeValueJpy: 6500,
    prizeWeightKg: 0.65,
    drawDate: '2026-10-30',
    eligibilityRule: 'all_suites',
    vipMultiplierEnabled: true,
    status: 'Ativo',
    createdAt: '2026-09-01T10:00:00.000Z'
  },
  {
    id: 'giveaway-vip-special',
    title: 'Sorteio VIP: Cupom de ¥5.000 no Frete EMS + Kit Gashapon Bandai Namco',
    description:
      'Sorteio exclusivo para suítes com pacotes recebidos ou assinantes dos planos PRO e Business. Chances multiplicadas por nível de plano!',
    prizeTitle: 'Kit 4 Gashapons Originais Bandai + Crédito de ¥5.000 no Frete Internacional',
    prizeValueJpy: 5000,
    prizeWeightKg: 0.35,
    drawDate: '2026-11-15',
    eligibilityRule: 'active_packages',
    vipMultiplierEnabled: true,
    status: 'Ativo',
    createdAt: '2026-09-15T10:00:00.000Z'
  },
  {
    id: 'giveaway-past-august',
    title: 'Sorteio de Agosto: Action Figure Banpresto One Piece Gear 5 (Luffy)',
    description:
      'Sorteio realizado ao vivo no painel do armazém em Saitama entre todas as suítes ativas do mês.',
    prizeTitle: 'Action Figure Original Banpresto One Piece King of Artist Luffy Gear 5',
    prizeValueJpy: 4800,
    prizeWeightKg: 0.58,
    drawDate: '2026-08-31',
    eligibilityRule: 'all_suites',
    vipMultiplierEnabled: true,
    status: 'Entregue na Suíte',
    winnerSuiteNumber: 'DJ-24950',
    winnerUserName: 'Lucas M. (São Paulo - SP)',
    drawnAt: '2026-08-31T18:30:00.000Z',
    packageDeposited: true,
    createdAt: '2026-08-01T10:00:00.000Z'
  }
];

export function captureReferralFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const refParam = params.get('ref') || params.get('suite') || params.get('convite');
    if (refParam && refParam.trim()) {
      const normalized = normalizeSuiteCode(refParam);
      localStorage.setItem(STORAGE_KEYS.CAPTURED_REF, normalized);
      return normalized;
    }
    return localStorage.getItem(STORAGE_KEYS.CAPTURED_REF);
  } catch {
    return null;
  }
}

export function getStoredReferralSuite(): string {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem(STORAGE_KEYS.CAPTURED_REF) || '';
  } catch {
    return '';
  }
}

export function normalizeSuiteCode(raw: string): string {
  const cleaned = raw.trim().toUpperCase().replace(/[^A-Z0-9-]/g, '');
  if (!cleaned) return '';
  if (/^\d+$/.test(cleaned)) {
    return `DJ-${cleaned}`;
  }
  if (cleaned.startsWith('DJ') && !cleaned.startsWith('DJ-')) {
    return `DJ-${cleaned.slice(2)}`;
  }
  return cleaned;
}

export function loadLocalAffiliateConfig(): AffiliateProgramConfig {
  if (typeof window === 'undefined') return DEFAULT_AFFILIATE_CONFIG;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CONFIG);
    if (raw) {
      return { ...DEFAULT_AFFILIATE_CONFIG, ...JSON.parse(raw) };
    }
  } catch {}
  return DEFAULT_AFFILIATE_CONFIG;
}

export function saveLocalAffiliateConfig(config: AffiliateProgramConfig) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify(config));
  } catch {}
}

export function loadLocalReferrals(): AffiliateReferral[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.REFERRALS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return [];
}

export function saveLocalReferrals(list: AffiliateReferral[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEYS.REFERRALS, JSON.stringify(list));
  } catch {}
}

export function loadLocalPayouts(): AffiliatePayoutRequest[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PAYOUTS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return [];
}

export function saveLocalPayouts(list: AffiliatePayoutRequest[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEYS.PAYOUTS, JSON.stringify(list));
  } catch {}
}

export function loadLocalGiveaways(): SuiteGiveaway[] {
  if (typeof window === 'undefined') return DEFAULT_SUITE_GIVEAWAYS;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.GIVEAWAYS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  return DEFAULT_SUITE_GIVEAWAYS;
}

export function saveLocalGiveaways(list: SuiteGiveaway[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEYS.GIVEAWAYS, JSON.stringify(list));
  } catch {}
}

export function subscribeAffiliateData(callbacks: {
  onConfig: (cfg: AffiliateProgramConfig) => void;
  onReferrals: (refs: AffiliateReferral[]) => void;
  onPayouts: (payouts: AffiliatePayoutRequest[]) => void;
  onGiveaways: (giveaways: SuiteGiveaway[]) => void;
}): () => void {
  callbacks.onConfig(loadLocalAffiliateConfig());
  callbacks.onReferrals(loadLocalReferrals());
  callbacks.onPayouts(loadLocalPayouts());
  callbacks.onGiveaways(loadLocalGiveaways());

  const unsubConfig = onSnapshot(
    doc(db, 'settings', 'affiliate_program'),
    (snap) => {
      if (snap.exists()) {
        const cfg = { ...DEFAULT_AFFILIATE_CONFIG, ...(snap.data() as AffiliateProgramConfig) };
        saveLocalAffiliateConfig(cfg);
        callbacks.onConfig(cfg);
      }
    },
    () => {}
  );

  const unsubRefs = onSnapshot(
    collection(db, 'affiliate_referrals'),
    (snap) => {
      if (!snap.empty) {
        const items: AffiliateReferral[] = [];
        snap.forEach((d) => items.push({ id: d.id, ...(d.data() as Omit<AffiliateReferral, 'id'>) }));
        items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        saveLocalReferrals(items);
        callbacks.onReferrals(items);
      }
    },
    () => {}
  );

  const unsubPayouts = onSnapshot(
    collection(db, 'affiliate_payouts'),
    (snap) => {
      if (!snap.empty) {
        const items: AffiliatePayoutRequest[] = [];
        snap.forEach((d) => items.push({ id: d.id, ...(d.data() as Omit<AffiliatePayoutRequest, 'id'>) }));
        items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        saveLocalPayouts(items);
        callbacks.onPayouts(items);
      }
    },
    () => {}
  );

  const unsubGiveaways = onSnapshot(
    collection(db, 'suite_giveaways'),
    (snap) => {
      if (!snap.empty) {
        const items: SuiteGiveaway[] = [];
        snap.forEach((d) => items.push({ id: d.id, ...(d.data() as Omit<SuiteGiveaway, 'id'>) }));
        items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        saveLocalGiveaways(items);
        callbacks.onGiveaways(items);
      }
    },
    () => {}
  );

  return () => {
    unsubConfig();
    unsubRefs();
    unsubPayouts();
    unsubGiveaways();
  };
}

export async function saveAffiliateProgramConfig(config: AffiliateProgramConfig): Promise<void> {
  const updated = { ...config, updatedAt: new Date().toISOString() };
  saveLocalAffiliateConfig(updated);
  try {
    await setDoc(doc(db, 'settings', 'affiliate_program'), updated, { merge: true });
  } catch (e) {
    console.warn('Saved affiliate config locally:', e);
  }
}

export async function saveAffiliateReferral(referral: AffiliateReferral): Promise<void> {
  const current = loadLocalReferrals();
  const idx = current.findIndex((r) => r.id === referral.id);
  const next = idx >= 0 ? current.map((r) => (r.id === referral.id ? referral : r)) : [referral, ...current];
  saveLocalReferrals(next);
  try {
    await setDoc(doc(db, 'affiliate_referrals', referral.id), referral, { merge: true });
  } catch (e) {
    console.warn('Saved referral locally:', e);
  }
}

export async function deleteAffiliateReferral(referralId: string): Promise<void> {
  const next = loadLocalReferrals().filter((r) => r.id !== referralId);
  saveLocalReferrals(next);
  try {
    await deleteDoc(doc(db, 'affiliate_referrals', referralId));
  } catch (e) {
    console.warn('Deleted referral locally:', e);
  }
}

export async function saveAffiliatePayout(payout: AffiliatePayoutRequest): Promise<void> {
  const current = loadLocalPayouts();
  const idx = current.findIndex((p) => p.id === payout.id);
  const next = idx >= 0 ? current.map((p) => (p.id === payout.id ? payout : p)) : [payout, ...current];
  saveLocalPayouts(next);
  try {
    await setDoc(doc(db, 'affiliate_payouts', payout.id), payout, { merge: true });
  } catch (e) {
    console.warn('Saved payout locally:', e);
  }
}

export async function saveSuiteGiveaway(giveaway: SuiteGiveaway): Promise<void> {
  const current = loadLocalGiveaways();
  const idx = current.findIndex((g) => g.id === giveaway.id);
  const next = idx >= 0 ? current.map((g) => (g.id === giveaway.id ? giveaway : g)) : [giveaway, ...current];
  saveLocalGiveaways(next);
  try {
    await setDoc(doc(db, 'suite_giveaways', giveaway.id), giveaway, { merge: true });
  } catch (e) {
    console.warn('Saved giveaway locally:', e);
  }
}

export async function deleteSuiteGiveaway(giveawayId: string): Promise<void> {
  const next = loadLocalGiveaways().filter((g) => g.id !== giveawayId);
  saveLocalGiveaways(next);
  try {
    await deleteDoc(doc(db, 'suite_giveaways', giveawayId));
  } catch (e) {
    console.warn('Deleted giveaway locally:', e);
  }
}

/**
 * Registers a referral when a new user signs up with a referrer Suite code (DJ-XXXX)
 */
export async function registerReferralForNewUser(params: {
  newUser: UserProfile;
  referrerSuiteInput: string;
  allUsers: UserProfile[];
}): Promise<AffiliateReferral | null> {
  const cleanRefSuite = normalizeSuiteCode(params.referrerSuiteInput);
  if (!cleanRefSuite || cleanRefSuite === 'DJ-ADMIN' || cleanRefSuite === params.newUser.suiteNumber) {
    return null;
  }

  const config = loadLocalAffiliateConfig();
  let referrerUser = params.allUsers.find(
    (u) => normalizeSuiteCode(u.suiteNumber || '') === cleanRefSuite
  );

  if (!referrerUser) {
    try {
      const snap = await getDocs(collection(db, 'users'));
      snap.forEach((d) => {
        const u = d.data() as UserProfile;
        if (normalizeSuiteCode(u.suiteNumber || '') === cleanRefSuite) {
          referrerUser = { ...u, uid: d.id };
        }
      });
    } catch {}
  }

  const isInfluencer =
    referrerUser?.subscriptionPlan === 'business' || referrerUser?.subscriptionPlan === 'pro';

  const referral: AffiliateReferral = {
    id: `ref-${Date.now()}`,
    referrerSuite: cleanRefSuite,
    referrerUid: referrerUser?.uid || `suite-${cleanRefSuite}`,
    referrerName: referrerUser?.name || `Cliente Suíte ${cleanRefSuite}`,
    referredSuite: params.newUser.suiteNumber,
    referredUid: params.newUser.uid,
    referredName: params.newUser.name,
    referredEmail: params.newUser.email,
    status: 'pendente',
    rewardJpy: config.referrerRewardJpy,
    welcomeBonusJpy: config.referredWelcomeBonusJpy,
    tier: isInfluencer ? 'influencer' : 'client',
    createdAt: new Date().toISOString()
  };

  await saveAffiliateReferral(referral);
  return referral;
}

/**
 * Calculates eligible suites and their weighted ticket counts for a giveaway draw
 */
export function calculateEligibleSuitesForGiveaway(
  giveaway: SuiteGiveaway,
  allUsers: UserProfile[],
  packages: PackageItem[],
  shipments: ShipmentOrder[]
): EligibleSuiteEntry[] {
  const clientUsers = allUsers.filter(
    (u) =>
      u.role !== 'admin' &&
      u.suiteNumber &&
      u.suiteNumber.toUpperCase() !== 'DJ-ADMIN'
  );

  const entries: EligibleSuiteEntry[] = [];

  for (const user of clientUsers) {
    const suiteUpper = user.suiteNumber.trim().toUpperCase();
    const userPackages = packages.filter(
      (p) => (p.suiteNumber || '').trim().toUpperCase() === suiteUpper || p.userId === user.uid
    );
    const userShipments = shipments.filter(
      (s) => (s.suiteNumber || '').trim().toUpperCase() === suiteUpper || s.userId === user.uid
    );

    const plan = user.subscriptionPlan || 'free';
    const hasActivity = userPackages.length > 0 || userShipments.length > 0;
    const isVip = plan === 'pro' || plan === 'business';
    const isManualIncluded = (giveaway.manualParticipantSuites || []).some(
      (s) => normalizeSuiteCode(s) === suiteUpper
    );

    let eligible = false;
    let reason = 'Suíte Ativa Cadastrada';

    if (giveaway.eligibilityRule === 'all_suites') {
      eligible = true;
      reason = isVip
        ? `Assinante ${plan.toUpperCase()} · Suíte Ativa`
        : hasActivity
        ? `${userPackages.length} pacote(s) no histórico`
        : 'Suíte Cadastrada';
    } else if (giveaway.eligibilityRule === 'active_packages') {
      if (hasActivity || isVip || isManualIncluded) {
        eligible = true;
        reason = hasActivity
          ? `${userPackages.length} pacote(s) / ${userShipments.length} envio(s)`
          : `Assinante ${plan.toUpperCase()}`;
      }
    } else if (giveaway.eligibilityRule === 'vip_only') {
      if (isVip || isManualIncluded) {
        eligible = true;
        reason = `Assinante Plano ${plan.toUpperCase()}`;
      }
    }

    if (!eligible) continue;

    let tickets = 1;
    if (giveaway.vipMultiplierEnabled) {
      if (plan === 'business') tickets = 5;
      else if (plan === 'pro') tickets = 3;
      else tickets = 1;

      if (userShipments.length > 0) {
        tickets += 1; // +1 extra ticket for clients who have shipped a box
      }
    }

    entries.push({
      uid: user.uid,
      suiteNumber: suiteUpper,
      name: user.name || `Cliente ${suiteUpper}`,
      email: user.email || '',
      plan,
      packagesCount: userPackages.length,
      shipmentsCount: userShipments.length,
      ticketsCount: tickets,
      eligibilityReason: reason
    });
  }

  // Also include any manual participant suites that might not be in allUsers yet
  for (const rawManual of giveaway.manualParticipantSuites || []) {
    const norm = normalizeSuiteCode(rawManual);
    if (norm && !entries.some((e) => e.suiteNumber === norm)) {
      entries.push({
        uid: `manual-${norm}`,
        suiteNumber: norm,
        name: `Suíte Participante ${norm}`,
        email: '',
        plan: 'free',
        packagesCount: 1,
        shipmentsCount: 0,
        ticketsCount: 1,
        eligibilityReason: 'Suíte incluída manualmente pelo Admin'
      });
    }
  }

  return entries.sort((a, b) => b.ticketsCount - a.ticketsCount);
}
